// URL utilities for handling tag filter parameters
import type { Id } from '../../convex/_generated/dataModel';

interface TagFilterParams {
  tags: string[];
  apps: string[];
  search?: string;
}

interface ParsedTagFilter {
  id: string;
  name: string;
  negative?: boolean;
}

interface ParsedAppFilter {
  id: string;
  name: string;
  negative?: boolean;
}

interface ParsedUrlFilters {
  tags: ParsedTagFilter[];
  apps: ParsedAppFilter[];
  search?: string;
}

/**
 * Parse URL search parameters to extract filter information
 */
export function parseUrlFilters(searchParams: URLSearchParams): TagFilterParams {
  const tagsParam = searchParams.get('tags');
  const appsParam = searchParams.get('apps');
  const searchParam = searchParams.get('search');

  return {
    tags: tagsParam ? tagsParam.split(',').filter(Boolean) : [],
    apps: appsParam ? appsParam.split(',').filter(Boolean) : [],
    search: searchParam || undefined
  };
}

/**
 * Generate URL search parameters from filter state
 */
export function generateUrlParams(filters: {
  tags: Array<{ name: string; negative?: boolean }>;
  apps: Array<{ name: string; negative?: boolean }>;
  searchText?: string;
}): URLSearchParams {
  const params = new URLSearchParams();

  // Add tag filters
  if (filters.tags.length > 0) {
    const tagNames = filters.tags.map(tag => 
      tag.negative ? `-${tag.name}` : tag.name
    );
    params.set('tags', tagNames.join(','));
  }

  // Add app filters
  if (filters.apps.length > 0) {
    const appNames = filters.apps.map(app => 
      app.negative ? `-${app.name}` : app.name
    );
    params.set('apps', appNames.join(','));
  }

  // Add search text
  if (filters.searchText?.trim()) {
    params.set('search', filters.searchText.trim());
  }

  return params;
}

/**
 * Validate tag names against available tags
 */
export function validateTagFilters(
  tagNames: string[], 
  availableTags: Array<{ _id: Id<'tags'>; name: string }>
): ParsedTagFilter[] {
  return tagNames.map(tagName => {
    const negative = tagName.startsWith('-');
    const cleanName = negative ? tagName.slice(1) : tagName;
    const tag = availableTags.find(t => t.name.toLowerCase() === cleanName.toLowerCase());
    
    return {
      id: tag?._id || cleanName, // Fallback to name if tag not found
      name: tag?.name || cleanName,
      negative
    };
  }).filter(tag => tag.name); // Remove empty names
}

/**
 * Validate app names against available apps
 */
export function validateAppFilters(
  appNames: string[], 
  availableApps: Array<{ _id: Id<'apps'>; name: string }>
): ParsedAppFilter[] {
  return appNames.map(appName => {
    const negative = appName.startsWith('-');
    const cleanName = negative ? appName.slice(1) : appName;
    const app = availableApps.find(a => a.name.toLowerCase() === cleanName.toLowerCase());
    
    return {
      id: app?._id || cleanName, // Fallback to name if app not found
      name: app?.name || cleanName,
      negative
    };
  }).filter(app => app.name); // Remove empty names
}

/**
 * Create a shareable URL with current filters
 */
export function createShareableUrl(
  baseUrl: string,
  pathname: string,
  filters: {
    tags: Array<{ name: string; negative?: boolean }>;
    apps: Array<{ name: string; negative?: boolean }>;
    searchText?: string;
  }
): string {
  const params = generateUrlParams(filters);
  const queryString = params.toString();
  return `${baseUrl}${pathname}${queryString ? `?${queryString}` : ''}`;
}

/**
 * Sanitize tag/app names for URL use
 */
function sanitizeForUrl(name: string): string {
  return encodeURIComponent(name.replace(/\s+/g, '-').toLowerCase());
}

/**
 * Restore tag/app names from URL
 */
function restoreFromUrl(urlName: string): string {
  return decodeURIComponent(urlName).replace(/-/g, ' ');
}
